import type {
  CompanyPlanDto,
  PlanCatalogueDto,
  PlanDto,
  PlanFeature,
  PlanId,
  PlanLimit,
  PlanPriceDto,
  PlanRefusalDto,
  PlanUsageDto,
} from './generated/Api';

/**
 * Reading the plan catalogue (`GET /plans`) the same way in both apps.
 *
 * What each plan includes and allows is decided once, in the backend's
 * `plans.ts`, and served here. These helpers only look things up in it and
 * word a gate the way the backend words its refusal, so the prompt before an
 * action and the 403 after it say the same thing. Never hard-code a plan's
 * name, limit or price in an app: each screen used to read its own Stripe
 * product metadata, and each read it differently.
 */

/**
 * The plan a company is on while it pays for none, as the backend's
 * `FREE_PLAN`. Sign-up checks it out at its $0 Stripe price, so the owner
 * leaves a card; a company with no subscription at all is on it too.
 */
export const FREE_PLAN: PlanId = 'FREE';

/** A plan's entry in the catalogue. */
export function findPlan(
  catalogue: PlanCatalogueDto,
  id: PlanId
): PlanDto | undefined {
  return catalogue.plans.find((plan) => plan.id === id);
}

/** The cheapest plan that includes a feature, for "Video is on Pro". */
export function cheapestPlanWith(
  catalogue: PlanCatalogueDto,
  feature: PlanFeature
): PlanDto | null {
  // The catalogue lists plans cheapest first.
  return (
    catalogue.plans.find((plan) => plan.features.includes(feature)) ?? null
  );
}

/**
 * The cheapest plan that allows more of a limit than `id` does, or null when
 * `id` is already unlimited or nothing allows more.
 */
export function nextPlanFor(
  catalogue: PlanCatalogueDto,
  id: PlanId,
  limit: PlanLimit
): PlanDto | null {
  const current = findPlan(catalogue, id)?.limits[limit];
  if (current === null || current === undefined) return null;
  return (
    catalogue.plans.find((plan) => {
      const allowed = plan.limits[limit];
      return allowed === null || allowed > current;
    }) ?? null
  );
}

/** How a limit's unit reads: "calls this month". */
export function limitUnit(
  catalogue: PlanCatalogueDto,
  limit: PlanLimit
): string {
  return catalogue.limits.find((entry) => entry.id === limit)?.unit ?? limit;
}

/** "250", or "unlimited calls this month". */
function describeAllowance(
  catalogue: PlanCatalogueDto,
  plan: PlanDto,
  limit: PlanLimit
): string {
  const allowed = plan.limits[limit];
  return allowed === null
    ? `unlimited ${limitUnit(catalogue, limit)}`
    : `${allowed}`;
}

/** " Essential allows 250." after a limit, or nothing when no plan has more. */
function morePlan(
  catalogue: PlanCatalogueDto,
  id: PlanId,
  limit: PlanLimit
): string {
  const next = nextPlanFor(catalogue, id, limit);
  return next
    ? ` ${next.name} allows ${describeAllowance(catalogue, next, limit)}.`
    : '';
}

/** "Video is on the Pro plan. This company is on Starter." */
export function featureMessage(
  catalogue: PlanCatalogueDto,
  id: PlanId,
  feature: PlanFeature
): string {
  const name =
    catalogue.features.find((entry) => entry.id === feature)?.name ?? feature;
  const upgradeTo = cheapestPlanWith(catalogue, feature);
  const current = findPlan(catalogue, id)?.name ?? id;
  return upgradeTo
    ? `${name} is on the ${upgradeTo.name} plan. This company is on ${current}.`
    : `${name} is not on the ${current} plan.`;
}

/**
 * "The Starter plan allows 50 calls this month, and this company has 50.
 * Essential allows 250."
 */
export function limitMessage(
  catalogue: PlanCatalogueDto,
  id: PlanId,
  limit: PlanLimit,
  used: number
): string {
  const plan = findPlan(catalogue, id);
  const allowed = plan?.limits[limit];
  return `The ${plan?.name ?? id} plan allows ${allowed ?? 'unlimited'} ${limitUnit(catalogue, limit)}, and this company has ${used}.${morePlan(catalogue, id, limit)}`;
}

/**
 * Whether the company may use a feature now: what its plan includes, or
 * anything while the plan is not enforced. Through a company's grace the
 * server only logs what it would refuse, and a control the app hid or gated
 * would stop what the server takes.
 */
export function planAllows(
  catalogue: PlanCatalogueDto,
  plan: CompanyPlanDto,
  feature: PlanFeature
): boolean {
  if (!plan.enforced) return true;
  return !!findPlan(catalogue, plan.id)?.features.includes(feature);
}

/**
 * How many more of a limit the company may add now: Infinity when the plan
 * has no limit, or while it is not enforced (see planAllows).
 */
export function limitRemaining(usage: PlanUsageDto, limit: PlanLimit): number {
  const allowed = usage.limits[limit];
  if (!usage.plan.enforced || allowed === null) return Infinity;
  return allowed - usage.usage[limit];
}

/** Whether another one would go over the plan's limit, as it is enforced. */
export function limitReached(usage: PlanUsageDto, limit: PlanLimit): boolean {
  return limitRemaining(usage, limit) <= 0;
}

/** "January 1, 2027": the day a grace ends, as the owner was told it. */
function graceDay(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

export interface NearLimit {
  limit: PlanLimit;
  used: number;
  allowed: number;
  /** The plan that allows more, or null when none does. */
  next: PlanDto | null;
  /**
   * "You've used 41 of 50 calls this month. Essential allows 250." For a
   * company in its grace, when the limit starts to apply: "From January 1,
   * 2027, your plan's limits apply: 41 of 50 calls this month. Essential
   * allows 250."
   */
  message: string;
}

/**
 * Every limit the company has used at least `catalogue.nearLimit` of, for
 * the nudge to upgrade before a limit stops anyone, or, through a grace,
 * the warning of the day it will. A limit of 0 is a feature the plan
 * lacks, not one being used up, so it is left out.
 */
export function nearLimits(
  catalogue: PlanCatalogueDto,
  usage: PlanUsageDto
): NearLimit[] {
  return catalogue.limits.flatMap(({ id: limit }) => {
    const allowed = usage.limits[limit];
    const used = usage.usage[limit];
    if (allowed === null || allowed === 0) return [];
    if (used < allowed * catalogue.nearLimit) return [];
    return [
      {
        limit,
        used,
        allowed,
        next: nextPlanFor(catalogue, usage.plan.id, limit),
        message: usage.plan.graceUntil
          ? `From ${graceDay(usage.plan.graceUntil)}, your plan's limits apply: ${used} of ${allowed} ${limitUnit(catalogue, limit)}.${morePlan(catalogue, usage.plan.id, limit)}`
          : `You've used ${used} of ${allowed} ${limitUnit(catalogue, limit)}.${morePlan(catalogue, usage.plan.id, limit)}`,
      },
    ];
  });
}

export interface UsageMeterRow {
  limit: PlanLimit;
  /** The catalogue's name for it: "Calls a month". */
  name: string;
  used: number;
  /** Null is unlimited. */
  allowed: number | null;
  /** At least `catalogue.nearLimit` used, as `nearLimits` counts it. */
  near: boolean;
}

/**
 * Each of the plan's limits with how much is used, for a usage meter on the
 * web billing page and in mobile settings. A limit of 0 is a feature the
 * plan lacks, not one being used up, so it is left out.
 */
export function usageMeters(
  catalogue: PlanCatalogueDto,
  usage: PlanUsageDto
): UsageMeterRow[] {
  return catalogue.limits.flatMap(({ id: limit, name }) => {
    const allowed = usage.limits[limit];
    const used = usage.usage[limit];
    if (allowed === 0) return [];
    return [
      {
        limit,
        name,
        used,
        allowed,
        near: allowed !== null && used >= allowed * catalogue.nearLimit,
      },
    ];
  });
}

/**
 * The body of a 403 a plan refused, from an axios error, or null for any
 * other failure. Its `message` names the plan that would allow it; show it
 * as it is.
 */
export function planRefusalOf(error: unknown): PlanRefusalDto | null {
  const response = (
    error as { response?: { status?: number; data?: unknown } } | null
  )?.response;
  if (response?.status !== 403) return null;
  const body = response.data as Partial<PlanRefusalDto> | undefined;
  return body && typeof body.message === 'string' && 'upgradeTo' in body
    ? (body as PlanRefusalDto)
    : null;
}

/**
 * What a plan includes, one line each, for a plan card: each limit
 * ("Drivers: 5", "Calls a month: Unlimited") and each feature's name. A
 * limit of 0 is a feature the plan lacks, so it is left out.
 */
export function planHighlights(
  catalogue: PlanCatalogueDto,
  plan: PlanDto
): string[] {
  const limits = catalogue.limits.flatMap(({ id, name }) => {
    const allowed = plan.limits[id];
    if (allowed === 0) return [];
    return [`${name}: ${allowed === null ? 'Unlimited' : allowed}`];
  });
  const features = catalogue.features
    .filter((feature) => plan.features.includes(feature.id))
    .map((feature) => feature.name);
  return [...limits, ...features];
}

/**
 * A plan from the catalogue as the plan cards draw it (BillingPlanCard,
 * PlanPicker, PlanCard), in the dashboard and on the phone's sign-up: the
 * price in whole dollars (0 for Starter), and what it includes from the
 * plan itself. The cards used to read each product's Stripe metadata
 * (DRIVER_LIMIT, LIST_PRICE, the features), each screen in its own way.
 */
export function planCard(catalogue: PlanCatalogueDto, plan: PlanDto) {
  const interval = plan.price?.interval ?? 'month';
  return {
    id: plan.id,
    name: plan.name,
    description: '',
    price: plan.price ? Math.round(plan.price.unitAmount / 100) : null,
    interval,
    period: `/${interval}`,
    features: planHighlights(catalogue, plan),
    driverLimit: plan.limits.drivers,
    priceId: plan.price?.priceId ?? null,
  };
}

export type PlanCardData = ReturnType<typeof planCard>;

/**
 * The plans that can be picked, cheapest first: each one Stripe has a price
 * for. Starter's is $0, and it is checked out like the others so every
 * company leaves a card at sign-up; offering it with no price sent owners
 * to the dashboard without one. A plan Stripe did not answer with a price
 * for is left out rather than drawn as free.
 */
export function offeredPlans(catalogue: PlanCatalogueDto): PlanCardData[] {
  return catalogue.plans
    .filter((plan) => plan.price !== null)
    .map((plan) => planCard(catalogue, plan));
}

/** "$160 / month", from Stripe's price in cents. */
export function priceLine(price: PlanPriceDto | null): string | undefined {
  if (!price) return undefined;
  const amount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currency.toUpperCase(),
    maximumFractionDigits: price.unitAmount % 100 === 0 ? 0 : 2,
  }).format(price.unitAmount / 100);
  return `${amount} / ${price.interval}`;
}

/**
 * What an upgrade would be for: a feature the plan lacks, a limit it has
 * used up, or the one plan a refusal named (its `upgradeTo`).
 */
export type UpgradeFor =
  | { feature: PlanFeature }
  | { limit: PlanLimit }
  | { plan: PlanId };

/** A plan as an upsell's card draws it: the library's `PlanGatePlan`. */
export interface UpgradePlan {
  id: PlanId;
  name: string;
  price?: string;
  features: string[];
  recommended: boolean;
}

/**
 * The plan cards for an upsell: every plan that would give `current` what
 * it asked for, cheapest first as the catalogue orders them, the cheapest
 * marked as the one to pick. The web gates and the mobile sheets each built
 * their own list before, and listed different things on the cards.
 */
export function upgradePlans(
  catalogue: PlanCatalogueDto,
  current: PlanId,
  want: UpgradeFor
): UpgradePlan[] {
  const allowedNow =
    'limit' in want ? findPlan(catalogue, current)?.limits[want.limit] : null;
  return catalogue.plans
    .filter((plan) => {
      if (plan.id === current) return false;
      if ('feature' in want) return plan.features.includes(want.feature);
      if ('plan' in want) return plan.id === want.plan;
      // An unlimited plan has nothing more to sell on this limit.
      if (allowedNow === null || allowedNow === undefined) return false;
      const allowed = plan.limits[want.limit];
      return allowed === null || allowed > allowedNow;
    })
    .map((plan, index) => ({
      id: plan.id,
      name: plan.name,
      price: priceLine(plan.price),
      features: planHighlights(catalogue, plan),
      recommended: index === 0,
    }));
}
